<template>
  <div class="bg-white rounded border border-gray-200 p-3 mb-6">
    <form @submit.prevent="handleSubmit" class="flex flex-wrap items-end gap-3">
      <!-- ID Shooting -->
      <div class="w-40">
        <label class="text-xs font-medium text-black mb-1 block">ID Shooting</label>
        <input 
          v-model.trim="localFilters.idShooting"
          type="text" 
          placeholder="Ex: 159428" 
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" 
        />
      </div>

      <!-- Référence Produit -->
      <div class="w-48">
        <label class="text-xs font-medium text-black mb-1 block">Référence Produit</label>
        <input 
          v-model.trim="localFilters.reference"
          type="text" 
          placeholder="Ex: REF-123" 
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" 
        />
      </div>

      <!-- Bouton filtrer -->
      <button 
        type="submit" 
        :disabled="isLoading" 
        class="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <i class="fas fa-search mr-1"></i>
        {{ isLoading ? 'Recherche...' : 'Rechercher' }}
      </button>
      
      <!-- Bouton réinitialiser -->
      <button 
        type="button"
        @click="resetFilters"
        class="px-4 py-2 text-sm font-medium bg-gray-200 text-black rounded hover:bg-gray-300 transition-colors"
      >
        <i class="fas fa-times mr-1"></i>
        Réinitialiser
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import type { MelFilters } from '@/types'

interface Props {
  modelValue: MelFilters
  isLoading?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: MelFilters): void
  (e: 'filter', values: MelFilters): void
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false
})

const emit = defineEmits<Emits>()

// Local state pour les filtres (avec tous les champs initialisés)
const localFilters = ref<MelFilters>({ 
  idShooting: props.modelValue.idShooting || '',
  reference: props.modelValue.reference || '',
  confirmation: props.modelValue.confirmation || '',
  dateDemandeDebut: props.modelValue.dateDemandeDebut || '',
  dateDemandeFin: props.modelValue.dateDemandeFin || '',
  dateShootingDebut: props.modelValue.dateShootingDebut || '',
  dateShootingFin: props.modelValue.dateShootingFin || ''
})

// Watch pour synchroniser avec le parent
watch(() => props.modelValue, (newValue) => {
  localFilters.value = { 
    idShooting: newValue.idShooting || '',
    reference: newValue.reference || '',
    confirmation: newValue.confirmation || '',
    dateDemandeDebut: newValue.dateDemandeDebut || '',
    dateDemandeFin: newValue.dateDemandeFin || '',
    dateShootingDebut: newValue.dateShootingDebut || '',
    dateShootingFin: newValue.dateShootingFin || ''
  }
}, { deep: true })

// Watch pour émettre les changements vers le parent
watch(() => localFilters.value, (newValue) => {
  emit('update:modelValue', { ...newValue })
}, { deep: true })

// Fonction pour réinitialiser les filtres
const resetFilters = () => {
  const emptyFilters = {
    idShooting: '',
    reference: '',
    confirmation: '',
    dateDemandeDebut: '',
    dateDemandeFin: '',
    dateShootingDebut: '',
    dateShootingFin: ''
  }
  localFilters.value = emptyFilters
  emit('filter', emptyFilters)
}

// Émettre l'événement de filtrage au backend avec les valeurs
const handleSubmit = () => {
  emit('filter', { ...localFilters.value })
}
</script>

